import { FC } from 'react';
import {
  CaretDown,
  CaretUp,
  CountryFlag,
  PageHero,
  PageHeroNotification,
  TeamShirt,
  Typography,
} from '@dreamteam-monorepo/ui';
import { useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

export interface PlayerProfileHeroProps {
  notification?: PageHeroNotification;
  className?: string;
  isLoading?: boolean;
  firstName?: string;
  lastName?: string;
  position?: string | null;
  price?: number;
  priceChangeUp?: number;
  priceChangeDown?: number;
  teamName?: string | null;
  teamShortName?: string | null;
  shirtKey?: string | null;
  contestantFlagKey?: string | null;
}

export const PlayerProfilePageHero: FC<PlayerProfileHeroProps> = ({
  notification,
  className,
  isLoading,
  firstName,
  lastName,
  position,
  price,
  priceChangeUp,
  priceChangeDown,
  teamName,
  teamShortName,
  shirtKey,
  contestantFlagKey,
}) => {
  const navigate = useNavigate();

  const displayPrice = price ? `£${price.toFixed(1)}m` : '';
  const displayPriceChangeUp = priceChangeUp ? `£${priceChangeUp.toFixed(1)}m` : '';
  const displayPriceChangeDown = priceChangeDown ? `£${priceChangeDown.toFixed(1)}m` : '';

  const displayTeam = teamShortName || teamName || '';

  return (
    <PageHero notification={notification} className={className}>
      <div className="flex flex-col gap-[--padding-lg]">
        <button
          type="button"
          onClick={() => {
            trackFantasyEvent('primaryNavigation', { link_name: 'Player Profile Back' });
            navigate(-1);
          }}
          className="flex items-center gap-2 self-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-[--corner-xs]"
        >
          <ArrowLeft className="w-4 h-[14px] text-[--color-blue-200]" />
          <Typography
            as="span"
            variant="button020"
            className="text-[14px] leading-5 font-bold text-[--color-blue-200]"
          >
            Back
          </Typography>
        </button>
        <div className="flex flex-row gap-3 lg:gap-[--padding-lg]">
          {isLoading ? (
            <div className="h-[74px] w-[72px] lg:h-[120px] lg:w-[112px] shrink-0 rounded-[10px] bg-[--color-neutral-200] animate-pulse" />
          ) : (
            <TeamShirt
              assetScale="1x"
              team={shirtKey ?? undefined}
              position={position ?? undefined}
              className="h-[74px] w-[72px] lg:h-[120px] lg:w-[112px] pt-3 shrink-0 bg-[#60C5FB] rounded-[10px]"
              loading="eager"
            />
          )}
          <div className="flex flex-1 flex-col bg-[--page-header-bg-grey]">
            {isLoading ? (
              <div className="flex flex-col gap-2 animate-pulse">
                <div className="h-5 w-24 bg-[--color-neutral-300] rounded" />
                <div className="flex items-center gap-3 lg:gap-4 mb-[10px] lg:mb-6">
                  <div className="h-7 w-40 bg-[--color-neutral-300] rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="h-5 w-16 bg-[--color-neutral-300] rounded" />
                </div>
              </div>
            ) : (
              <>
                <Typography className="text-[14px] leading-[20px] lg:text-[20px] lg:leading-[26px] text-[--color-neutral-900] lg:mb-1">
                  {firstName || ''}
                </Typography>
                <div className="flex items-center gap-3 lg:gap-4 mb-[10px] lg:mb-6">
                  <Typography className="text-[14px] leading-[20px] lg:text-[28px] lg:leading-[32px] font-bold text-[--color-neutral-900]">
                    {lastName || ''}
                  </Typography>
                  {position && (
                    <span className="rounded-[4px] bg-white px-2 py-0.5 lg:py-2">
                      <Typography
                        variant="subheadline010"
                        className="text-[12px] leading-[16px] lg:text-[14px] lg:leading-[18px]"
                      >
                        {position}
                      </Typography>
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-[8px]">
                    <Typography
                      variant="subheadline030"
                      className="text-[16px] leading-[20px] lg:text-[20px] lg:leading-[24px]"
                    >
                      {displayPrice}
                    </Typography>
                    {!!priceChangeUp && (
                      <div className="flex items-center ml-2">
                        <Typography className="text-[10px] leading-[16px] lg:text-[14px] lg:leading-[20px]">
                          {displayPriceChangeUp}
                        </Typography>
                        <CaretUp width={20} height={20} className="text-[--color-green-200]" />
                      </div>
                    )}
                    {!!priceChangeDown && (
                      <div className="flex items-center">
                        <Typography className="text-[10px] leading-[16px] lg:text-[14px] lg:leading-[20px]">
                          {displayPriceChangeDown}
                        </Typography>
                        <CaretDown width={20} height={20} className="text-[--color-red-300]" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 lg:gap-2">
                    <Typography
                      variant="paragraph000"
                      className=" text-[10px] leading-[10px] lg:text-[14px] lg:leading-[20px] "
                    >
                      {displayTeam}
                    </Typography>
                    <div className="size-6 lg:size-9 overflow-hidden rounded-full">
                      {contestantFlagKey && (
                        <CountryFlag
                          country={contestantFlagKey}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PageHero>
  );
};
